/**
 * PDF Utility Functions
 * Handles PDF URL conversion and CORS bypass
 */

/**
 * Converts a Firebase Storage URL to use our proxy endpoint
 * This bypasses CORS issues when accessing Firebase Storage directly
 */
export const getProxiedPdfUrl = (originalUrl: string): string => {
  if (!originalUrl) return '';
  
  // If it's already a proxy URL, return as is
  if (originalUrl.includes('/pdf-proxy')) return originalUrl;
  
  // If it's a Firebase Storage URL, convert to proxy
  if (originalUrl.includes('firebasestorage.googleapis.com')) {
    const proxyUrl = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-profstudymate.cloudfunctions.net'}/pdfExtractor/pdf-proxy?pdfUrl=${encodeURIComponent(originalUrl)}`;
    return proxyUrl;
  }
  
  // For other URLs (like Google Drive), also use proxy for consistency
  if (originalUrl.includes('drive.google.com')) {
    const proxyUrl = `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-profstudymate.cloudfunctions.net'}/pdfExtractor/pdf-proxy?pdfUrl=${encodeURIComponent(originalUrl)}`;
    return proxyUrl;
  }
  
  // Return original URL for other cases
  return originalUrl;
};

/**
 * Checks if a URL needs proxying (Firebase Storage or Google Drive)
 */
export const needsProxying = (url: string): boolean => {
  return url.includes('firebasestorage.googleapis.com') || url.includes('drive.google.com');
};

/**
 * Gets the Firebase Functions base URL
 */
export const getFirebaseFunctionsUrl = (): string => {
  return process.env.REACT_APP_FIREBASE_FUNCTIONS_URL || 'https://us-central1-profstudymate.cloudfunctions.net';
};

/**
 * Creates a complete proxy URL for a PDF
 */
export const createProxyUrl = (pdfUrl: string): string => {
  const baseUrl = getFirebaseFunctionsUrl();
  return `${baseUrl}/pdfExtractor/pdf-proxy?pdfUrl=${encodeURIComponent(pdfUrl)}`;
};

/**
 * Ensures an image URL is properly formatted and accessible
 */
export const getImageUrl = (imageUrl: string | undefined): string | null => {
  if (!imageUrl) return null;
  
  // If it's already a full URL, return as is
  if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
    return imageUrl;
  }
  
  // If it's a relative path, try to construct a full URL
  if (imageUrl.startsWith('/')) {
    // This would be relative to your domain
    return `${window.location.origin}${imageUrl}`;
  }
  
  // If it's a Firebase Storage path, construct the full URL
  if (imageUrl.includes('firebasestorage.googleapis.com')) {
    return imageUrl;
  }
  
  // For other cases, return the original URL
  return imageUrl;
};
